<template>
    <div class="container-fluid">
        <Loading v-if="!loaded"></Loading>
        <div v-else>
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('pharmacies') }}</h3>
            </div>
            <!-- CREATE PHARMACY BUTTON -->
            <div class="row mt-4">
                <div class="col-sm-12">
                    <router-link :to="{ name: 'pharmacy.create' }" class="btn btn-md btn-success">
                        {{ $t('create_pharmacy') }}
                    </router-link>
                </div>
            </div>
            <!-- FILTERS -->
            <div class="form-group row mt-4">
                <label class="col-md-1 col-form-label" for="name">{{ $t('name') }}</label>
                <div class="col-md-2">
                    <input type="text" id="name" class="form-control" v-model="searchName" />
                </div>
                <label class="col-md-1 offset-1 col-form-label" for="status">{{ $t('status') }}</label>
                <div class="col-md-2">
                    <select id="status" class="form-control" v-model="searchStatus">
                        <option value="all" selected>{{ $t('all') }}</option>
                        <option value="active">{{ $t('active') }}</option>
                        <option value="inactive">{{ $t('inactive') }}</option>
                    </select>
                </div>
            </div>
            <!-- PHARMACIES TABLE -->
            <table class="table table-hover mt-4 md-3 table-striped">
                <thead>
                    <tr>
                        <th>{{ $t('business_id') }}</th>
                        <th>{{ $t('name') }}</th>
                        <th>{{ $t('drugsafe_users') }}</th>
                        <th>{{ $t('status') }}</th>
                        <th>{{ $t('owners') }}</th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody v-if="pharmacies">
                    <tr v-for="pharmacy in filteredPharmacies" v-bind:key="pharmacy.id">
                        <td class="align-middle">{{ pharmacy.information.business_id }}</td>
                        <td class="align-middle">{{ pharmacy.information.name }}</td>
                        <td class="align-middle">{{ pharmacy.users ? Object.keys(pharmacy.users).length : 0 }}</td>
                        <td class="align-middle">{{ $t(pharmacy.information.status) }}</td>
                        <td class="align-middle" style="max-width: 400px">
                            <span
                                class="badge badge-primary mr-sm-2"
                                v-for="owner in pharmacy.webUsers"
                                v-bind:key="owner.id"
                            >
                                <span v-if="owner.role === 'pharmacy_owner'">
                                    {{ owner.first_name }} {{ owner.last_name }}</span
                                >
                            </span>
                        </td>
                        <td class="align-middle">
                            <button
                                class="btn btn-sm btn-info"
                                v-on:click="$router.push({ name: 'pharmacy.edit', params: { id: pharmacy.id } })"
                                :disabled="submitClicked"
                            >
                                {{ $t('edit') }}
                            </button>
                        </td>
                        <td class="align-middle">
                            <button
                                class="btn btn-sm btn-danger ml-sm-3"
                                v-on:click="() => handleDeleteClick(pharmacy.id)"
                                :disabled="submitClicked"
                            >
                                {{ $t('delete') }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- MODAL: ARE YOU SURE YOU WANT TO DELETE PHARMACY? -->
        <b-modal id="deleteConfirmationModal" centered :title="$t('delete_pharmacy_are_you_sure_title')" hide-footer>
            <div class="row">
                <div class="col">
                    <p>{{ $t('delete_pharmacy_are_you_sure_text') }}</p>
                </div>
            </div>
            <div class="d-flex row mt-2">
                <div class="d-flex col justify-content-center">
                    <button class="btn btn-primary mr-4" @click="deletePharmacy">
                        {{ $t('ok') }}
                    </button>
                    <button class="btn btn-secondary" @click="handleDeleteCancel">
                        {{ $t('cancel') }}
                    </button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import Loading from '@/views/partial/Loading';
import { deletePharmacy, getPharmacies, handleAxiosError } from '@/services/axiosService';

export default {
    components: { Loading },
    data() {
        return {
            deletePharmacyId: null,
            loaded: false,
            pharmacies: [],
            searchName: '',
            searchStatus: 'all',
            submitClicked: false
        };
    },
    async created() {
        // get all pharmacies and their web users
        await this.getPharmacies('false');
        this.loaded = true;
    },
    computed: {
        filteredPharmacies() {
            return this.pharmacies.filter(item => {
                return (
                    item.information.name.toLowerCase().includes(this.searchName.toLowerCase()) &&
                    (this.searchStatus !== 'all' ? item.information.status === this.searchStatus : true)
                );
            });
        }
    },
    methods: {
        ...mapActions('account', ['logout']),
        // get all pharmacies and their web users
        async getPharmacies(getWebUsers) {
            try {
                this.pharmacies = await getPharmacies(getWebUsers);
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // delete pharmacy and its web users
        async deletePharmacy() {
            try {
                this.$bvModal.hide('deleteConfirmationModal'); // show "are you sure?" modal
                const noWebUsersFound = await deletePharmacy(this.deletePharmacyId);
                this.$toastr.s(this.$t('pharmacy_deleted'));
                if (!noWebUsersFound) {
                    this.$toastr.s(this.$t('pharmacy_web_users_deleted'));
                }
                // remove deleted pharmacy from list
                this.pharmacies = this.pharmacies.filter(pharmacy => {
                    return pharmacy.id !== this.deletePharmacyId;
                });
            } catch (error) {
                handleAxiosError(error);
            }
            this.handleDeleteCancel();
        },
        // handle "delete" cancellation
        handleDeleteCancel() {
            this.submitClicked = false;
            this.deletePharmacyId = null;
            this.$bvModal.hide('deleteConfirmationModal'); // hide modal
        },
        // handle "delete" click
        async handleDeleteClick(id) {
            this.submitClicked = true;
            this.deletePharmacyId = id;
            this.$bvModal.show('deleteConfirmationModal'); // show modal
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.handleDeleteCancel();
            }
        });
    }
};
</script>
